import React from 'react';
import VpIcon from './VpIcon';

// Small shared building blocks for the vendor portal.

export const VpSpinner = () => <div className='vp-spinner' />;

export const VpEmpty = ({
	icon = 'empty',
	text = 'Nothing here yet.',
}: {
	icon?: string;
	text?: string;
}) => (
	<div className='vp-empty'>
		<div className='vp-empty-icon'>
			<VpIcon name={icon} size={44} />
		</div>
		<div>{text}</div>
	</div>
);

export const VpStatusBadge = ({ status }: { status?: string }) => {
	const s = (status || '').toLowerCase();
	let cls = 'vp-badge-neutral';
	let label = status || '—';
	if (s === 'confirmed') {
		cls = 'vp-badge-confirmed';
		label = 'Accepted';
	} else if (s === 'rejected') {
		cls = 'vp-badge-rejected';
		label = 'Rejected';
	} else if (s === 'pending') {
		cls = 'vp-badge-pending';
		label = 'Pending';
	}
	return <span className={`vp-badge ${cls}`}>{label}</span>;
};

export const VpStat = ({
	icon,
	value,
	label,
	tint = 'primary',
}: {
	icon: string;
	value: React.ReactNode;
	label: string;
	tint?: 'primary' | 'success' | 'warning' | 'danger' | 'info';
}) => (
	<div className='vp-stat'>
		<div className='vp-stat-top'>
			<div className='vp-stat-value'>{value}</div>
			<div className={`vp-stat-icon vp-tint-${tint}`}>
				<VpIcon name={icon} size={22} />
			</div>
		</div>
		<div className='vp-stat-label'>{label}</div>
	</div>
);

export const VpConfirmModal = ({
	open,
	icon = 'warning',
	title,
	message,
	confirmText = 'Confirm',
	cancelText = 'Cancel',
	loading = false,
	onConfirm,
	onCancel,
}: {
	open: boolean;
	icon?: string;
	title: string;
	message: string;
	confirmText?: string;
	cancelText?: string;
	loading?: boolean;
	onConfirm: () => void;
	onCancel: () => void;
}) => {
	if (!open) return null;
	return (
		<div className='vp-modal-backdrop' onClick={onCancel}>
			<div className='vp-modal' onClick={(e) => e.stopPropagation()}>
				<div className='vp-modal-emoji'>
					<VpIcon name={icon} size={44} />
				</div>
				<h3>{title}</h3>
				<p>{message}</p>
				<div className='vp-modal-actions'>
					<button
						type='button'
						className='vp-btn vp-btn-ghost'
						onClick={onCancel}
						disabled={loading}>
						{cancelText}
					</button>
					<button
						type='button'
						className='vp-btn vp-btn-primary'
						onClick={onConfirm}
						disabled={loading}>
						{loading ? 'Please wait…' : confirmText}
					</button>
				</div>
			</div>
		</div>
	);
};

export const money = (n: any) => {
	const v = Number(n || 0);
	return `₹${v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const fmtDate = (d: any) => {
	if (!d) return '—';
	try {
		return new Date(d).toLocaleDateString('en-IN', {
			day: '2-digit',
			month: 'short',
			year: 'numeric',
		});
	} catch {
		return '—';
	}
};

// Simple pager: Prev / page x of y / Next. Hidden when there's a single page.
export const VpPagination = ({
	page,
	perPage,
	total,
	onPage,
}: {
	page: number;
	perPage: number;
	total: number;
	onPage: (p: number) => void;
}) => {
	const totalPages = Math.max(1, Math.ceil(total / perPage));
	if (total === 0) return null;
	const from = (page - 1) * perPage + 1;
	const to = Math.min(page * perPage, total);
	return (
		<div
			className='vp-row-between'
			style={{ padding: '14px 22px', borderTop: '1px solid var(--vp-border)' }}>
			<div className='vp-muted' style={{ fontSize: 13 }}>
				Showing {from}–{to} of {total}
			</div>
			<div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
				<button
					type='button'
					className='vp-btn vp-btn-ghost vp-input-sm'
					disabled={page <= 1}
					onClick={() => onPage(page - 1)}>
					<VpIcon name='prev' size={16} /> Prev
				</button>
				<span className='vp-muted' style={{ fontSize: 13 }}>
					Page {page} of {totalPages}
				</span>
				<button
					type='button'
					className='vp-btn vp-btn-ghost vp-input-sm'
					disabled={page >= totalPages}
					onClick={() => onPage(page + 1)}>
					Next <VpIcon name='next' size={16} />
				</button>
			</div>
		</div>
	);
};
